import { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";

// Normaliza para pesquisa acento- e case-insensível
const norm = (s) => (s || "")
  .toString()
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "");

/**
 * Combobox de pesquisa para dropdowns longos (ex: 240 consultores, dezenas de equipas).
 *
 * Props:
 *  - options: Array<{ id, label, sublabel? }>
 *  - value: string  (id selecionado, "" = vazio)
 *  - onChange: (id) => void
 *  - placeholder?: string (default: "Escrever para procurar…")
 *  - emptyOption?: string (default: null — sem opção de "vazio")
 *  - testid?: string
 */
export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Escrever para procurar…",
  emptyOption = null,
  testid,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  // Ordena por label (PT-PT collation) e filtra pela query
  const sorted = useMemo(
    () => [...options].sort((a, b) =>
      (a.label || "").localeCompare(b.label || "", "pt", { sensitivity: "base" }),
    ),
    [options],
  );
  const filtered = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return sorted;
    return sorted.filter((o) => {
      const hay = norm(`${o.label} ${o.sublabel || ""}`);
      return hay.includes(q);
    });
  }, [sorted, query]);

  // Espelhar selecção externa no input
  const selected = sorted.find((o) => o.id === value);
  useEffect(() => {
    setQuery(selected ? selected.label : "");
  }, [selected]);

  const pick = (o) => {
    onChange(o ? o.id : "");
    setQuery(o ? o.label : "");
    setOpen(false);
  };

  const clear = () => {
    onChange("");
    setQuery("");
    setOpen(true);
  };

  return (
    <div className="relative" data-testid={testid}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373] pointer-events-none" />
        <input
          type="text"
          value={query}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          placeholder={placeholder}
          className="kw-input pl-9 pr-9"
          autoComplete="new-password"
          autoCorrect="off"
          spellCheck="false"
          name={`searchable-${testid || Math.random().toString(36).slice(2)}`}
          data-form-type="other"
          data-lpignore="true"
          data-testid={testid ? `${testid}-input` : undefined}
        />
        {query && (
          <button type="button" onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#737373] hover:text-white"
            aria-label="Limpar"
            data-testid={testid ? `${testid}-clear` : undefined}>
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-[#0A0A0A] border border-[#333] shadow-lg"
          data-testid={testid ? `${testid}-options` : undefined}
        >
          {emptyOption && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(null)}
              className={`w-full text-left px-3 py-2 hover:bg-[#1A1A1A] text-xs uppercase tracking-widest text-[#737373] border-b border-[#1A1A1A] ${
                !value ? "bg-[#1A1A1A]" : ""
              }`}
            >
              {emptyOption}
            </button>
          )}
          {filtered.length === 0 && (
            <div className="px-3 py-3 text-xs text-[#737373]">Sem resultados.</div>
          )}
          {filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o)}
              className={`w-full text-left px-3 py-2 hover:bg-[#1A1A1A] border-b border-[#1A1A1A] ${
                o.id === value ? "bg-[#1A1A1A]" : ""
              }`}
              data-testid={testid ? `${testid}-option-${o.id}` : undefined}
            >
              <div className="font-semibold text-sm truncate">{o.label}</div>
              {o.sublabel && (
                <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-0.5">
                  {o.sublabel}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
